import * as React from "react";
import { useQuery } from "react-apollo";

import styled from "styled-components";
import { Button } from "@mui/material";
import { ReportFilterValues } from "../../views/App/ReportingOverviewWithFilters";
import gql from "graphql-tag";
import {
    GetReportsBasedOnParams,
    GetReportsBasedOnParams_reportsWithParams,
    GetReportsBasedOnParamsVariables,
} from "./__generated__/GetReportsBasedOnParams";
import NoReportsFound from "./NoReportsFound";
import ReportOverviewItem from "./ReportOverviewItem";

export const GET_REPORTS_BASED_ON_PARAMS = gql`
    query GetReportsBasedOnParams(
        $organizationId: ID!
        $start: String
        $end: String
        $page: Int!
        $search: String
        $orderBy: String!
        $orderDirection: OrderDirectionType!
        $limit: Int!
    ) {
        reportsWithParams(
            organizationId: $organizationId
            start: $start
            end: $end
            search: $search
            page: $page
            orderBy: $orderBy
            orderDirection: $orderDirection
            limit: $limit
        ) {
            reportId
            organizationId
            reportGenerated
            name
            start
            end
            clientId
        }
    }
`;

interface Props {
    organizationId: string;
    filters: ReportFilterValues;
}

const Wrapper = styled.div`
    display: flex;
    flex-direction: column;
`;

const More = styled.div`
    margin: 10px 0px;
    display: flex;
    width: 100%;
    justify-content: center;
`;

export default function ReportOverviewList({ filters, organizationId }: Props) {
    const [list, setList] = React.useState<GetReportsBasedOnParams_reportsWithParams[]>([]);
    const [showMore, setShowMore] = React.useState<boolean>(false);

    const USED_LIMIT = 20;

    const { endDate, search, sortBy, sortDirection, startDate } = filters;

    const { data, fetchMore } = useQuery<GetReportsBasedOnParams, GetReportsBasedOnParamsVariables>(
        GET_REPORTS_BASED_ON_PARAMS,
        {
            variables: {
                limit: USED_LIMIT,
                orderBy: sortBy,
                orderDirection: sortDirection,
                organizationId: organizationId!,
                page: 1,
                end: endDate,
                start: startDate,
                ...(search.trim().length > 0 && { search }),
            },
            onCompleted(data) {
                setList(data.reportsWithParams);
                setShowMore(data.reportsWithParams.length >= USED_LIMIT);
            },
        },
    );

    function onMoreClick() {
        const reports = data!.reportsWithParams;
        fetchMore({
            variables: {
                limit: USED_LIMIT,
                orderBy: sortBy,
                orderDirection: sortDirection,
                organizationId: organizationId!,
                page: parseInt((reports.length / USED_LIMIT).toString()) + 1,
                end: endDate,
                start: startDate,
                ...(search.trim().length > 0 && { search }),
            },
            updateQuery: (previousQueryResult, { fetchMoreResult }) => {
                if (!fetchMoreResult) {
                    return previousQueryResult;
                }

                const res: GetReportsBasedOnParams = {
                    reportsWithParams: [...previousQueryResult.reportsWithParams, ...fetchMoreResult.reportsWithParams],
                };

                setList(res.reportsWithParams);

                if (
                    fetchMoreResult.reportsWithParams.length < USED_LIMIT ||
                    res.reportsWithParams.length === reports.length
                ) {
                    setShowMore(false);
                }
                return res;
            },
        });
    }

    return (
        <>
            <Wrapper>
                {list.length === 0 && <NoReportsFound />}
                {list.length > 0 &&
                    list.map((report) => <ReportOverviewItem key={`report-${report.reportId}`} item={report} />)}
                <More>
                    {showMore && (
                        <Button color="secondary" onClick={onMoreClick}>
                            Load more
                        </Button>
                    )}
                </More>
            </Wrapper>
        </>
    );
}
